var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Hook for fetching a single `module_setting` row by key (TASK-177
 * `GET /czna-authorities/module-settings/{key}`).
 *
 * Used by the Settings UI editor (TASK-180). Distinct from `useFormConfig`
 * (TASK-178) which is the renderer-side hook and turns every non-200 outcome
 * into an explicit unavailable state (CZNA-1292; before that, a silent fallback
 * to the bundled TS registry) — here we surface the discriminator so the
 * editor can render the right placeholder.
 *
 * MEMORY rule: `useOkapiKy()` returns an unstable reference. The ky
 * instance is captured in the query function closure and is NEVER listed
 * in any dependency array — listing it triggers an infinite refetch loop.
 *
 * Cache policy: `staleTime: 0` (Settings UI requires fresh state),
 * `refetchOnWindowFocus: true`, `retry: false`.
 *
 * Query key: always `moduleSettingQueryKey(key)`. Until CZNA-342 the
 * `form_config.*` keys deliberately reused `useFormConfig`'s key so that one
 * `invalidateQueries` reached both the editor and any open RecordForm — but
 * the two hooks read different endpoints with different payload shapes, so
 * sharing the key meant sharing the cached body, and the loser rendered the
 * wrong thing (see `effectiveFormConfigQueryKey`). Cross-invalidation is now
 * explicit via `moduleSettingInvalidationKeys`.
 */
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { DEFAULT_FORM_CONFIG_TEMPLATE_ID } from '../types/api';
import { effectiveFormConfigQueryKey } from './useFormConfig';
import { formConfigTemplatesQueryKey } from './useFormConfigTemplates';
import { helpTextsQueryKey } from './useHelpTexts';
import { classifyModuleSettingError, MODULE_SETTING_ERROR_REASONS } from './_moduleSettingErrors';
var FORM_CONFIG_KEY_PREFIX = 'form_config.';
var HELP_TEXT_OVERRIDES_KEY = 'help_text.overrides';
/** Stable query-key factory for the raw `module_setting` row behind `key`. */
export var moduleSettingQueryKey = function (key) {
    return ['module-setting', key];
};
/**
 * The query key this hook reads under. Kept as a named export because the
 * editors and mutation hooks referred to it by this name before CZNA-342; it
 * is now simply `moduleSettingQueryKey` for every key, `form_config.*`
 * included.
 */
export var resolveModuleSettingQueryKey = function (key) {
    return moduleSettingQueryKey(key);
};
/**
 * Every cache entry a write to `key` makes stale.
 *
 * For `form_config.*` that is more than one: the raw template the editor reads,
 * and the compiled EFC the record form reads — saving the template changes what
 * the compiler emits, so both must refetch. They are separate keys on purpose
 * (CZNA-342); this function is what keeps the one-invalidation-reaches-both
 * behaviour the shared key used to provide.
 *
 * Every write path (`useSaveModuleSetting`, `useResetModuleSetting`,
 * `useDeleteModuleSetting`, `useRenameFormConfigTemplate`, and the two editors'
 * post-import invalidations) must invalidate ALL of these — a write that
 * invalidates only the editor's key leaves an open record form showing the
 * pre-save config until its staleTime lapses.
 *
 * <b>CZNA-456 — the key now has three segments, and parsing it as two was a
 * live bug.</b> This function used to take everything after `form_config.` as
 * the record type. Once changeset 101 renamed the rows to
 * `form_config.{TYPE}.{templateId}`, that produced the type `"SUBJECT.default"`
 * and an EFC key of `['module-setting','effective','SUBJECT.default']` — which
 * matches nothing, since the reader keys on `'SUBJECT'`. The observable effect
 * was not an error anywhere: saving a form in Settings simply stopped
 * refreshing an open record form, for up to `useFormConfig`'s five-minute
 * staleTime. Split on the FIRST dot only; the type never contains one and the
 * template id cannot (R4 restricts it to `[a-z0-9-]`).
 */
export var moduleSettingInvalidationKeys = function (key) {
    var keys = [__spreadArray([], moduleSettingQueryKey(key), true)];
    if (key.startsWith(FORM_CONFIG_KEY_PREFIX)) {
        var rest = key.substring(FORM_CONFIG_KEY_PREFIX.length);
        var dot = rest.indexOf('.');
        var type = (dot === -1 ? rest : rest.substring(0, dot));
        var templateId = dot === -1 ? undefined : rest.substring(dot + 1);
        // The DEFAULT template is what `GET /effective/{type}` (no template
        // segment) compiles, so writing it invalidates the template-less key that
        // every ordinary form reader uses. A pre-CZNA-456 two-segment key is
        // treated the same way — it can only mean the default.
        if (templateId === undefined || templateId === DEFAULT_FORM_CONFIG_TEMPLATE_ID) {
            keys.push(__spreadArray([], effectiveFormConfigQueryKey(type), true));
        }
        // A named template additionally has its own compiled entry.
        if (templateId !== undefined) {
            keys.push(__spreadArray([], effectiveFormConfigQueryKey(type, templateId), true));
        }
        // The template lists carry `templateLabel`, `customized` and `updatedAt`,
        // all of which a write can change — both the per-type slot the settings
        // page reads and the unfiltered slot the create dialog reads.
        keys.push(__spreadArray([], formConfigTemplatesQueryKey(type), true));
        keys.push(__spreadArray([], formConfigTemplatesQueryKey(), true));
    }
    // CZNA-407: the module-wide help layer reads the same row through its own route, so a
    // save in the help-text editor must refresh it, or the edited text would only show up
    // after a reload.
    if (key === HELP_TEXT_OVERRIDES_KEY) {
        keys.push(__spreadArray([], helpTextsQueryKey, true));
    }
    return keys;
};
var ModuleSettingFetchError = /** @class */ (function (_super) {
    __extends(ModuleSettingFetchError, _super);
    function ModuleSettingFetchError(reason, cause) {
        var _this = _super.call(this, "useModuleSetting fetch failed: ".concat(reason)) || this;
        _this.name = 'ModuleSettingFetchError';
        _this.reason = reason;
        if (cause !== undefined) {
            _this.cause = cause;
        }
        return _this;
    }
    return ModuleSettingFetchError;
}(Error));
var useModuleSetting = function (key) {
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    var _a = useQuery(resolveModuleSettingQueryKey(key), function () { return __awaiter(void 0, void 0, void 0, function () {
        var response, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, ky
                            .get("czna-authorities/module-settings/".concat(key))
                            .json()];
                case 1:
                    response = _a.sent();
                    return [2 /*return*/, response];
                case 2:
                    err_1 = _a.sent();
                    throw new ModuleSettingFetchError(classifyModuleSettingError(err_1), err_1);
                case 3: return [2 /*return*/];
            }
        });
    }); }, {
        enabled: Boolean(key),
        staleTime: 0,
        refetchOnWindowFocus: true,
        retry: false,
    }), data = _a.data, isLoading = _a.isLoading, isError = _a.isError, error = _a.error;
    return {
        data: data,
        isLoading: isLoading,
        isError: isError,
        errorReason: isError && error ? error.reason : null,
    };
};
export { MODULE_SETTING_ERROR_REASONS };
export default useModuleSetting;
